// Types et interfaces pour la gestion des abonnements
export interface SubscriptionPlan {
  id: string;
  name: string;
  amount: number; // en cents
  currency: string;
  period: "monthly" | "yearly" | "lifetime";
  features: string[];
}

/**
 * `id` d'un abonnement qui n'existe pas côté facturation.
 *
 * Un compte sans ligne LemonSqueezy — Gratuit, ou en essai Solo — est tout de
 * même représenté par un `Subscription` pour que la page « Mon abonnement »
 * ait quelque chose à afficher. Cette sentinelle distingue ce cas d'un
 * abonnement réel, et c'est elle qui empêche de proposer « Annuler le
 * renouvellement » sur un abonnement inexistant.
 */
export const NO_BILLING_SUBSCRIPTION_ID = "default";

/** Vrai si l'abonnement correspond à une vraie ligne de facturation. */
export function hasRealBillingSubscription(
  subscription: Pick<Subscription, "id">,
): boolean {
  return (
    !!subscription.id && subscription.id !== NO_BILLING_SUBSCRIPTION_ID
  );
}

export interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  trialEnd?: Date;
}

export type SubscriptionStatus =
  | "active"
  | "canceled"
  | "past_due"
  | "trialing"
  | "incomplete";

export interface SubscriptionViewModel {
  // Affichage
  planName: string;
  amountLabel: string;
  periodLabel: string;
  statusLabel: string;
  statusClassName: string;

  // Dates
  nextBillingDate?: string;
  renewalDate?: string;
  trialEndDate?: string;

  // État
  status: SubscriptionStatus;
  isActive: boolean;
  isCanceled: boolean;
  isPastDue: boolean;
  isTrialing: boolean;
  isLifetime: boolean;

  // Actions disponibles
  canChangePlan: boolean;
  canCancelRenewal: boolean;
  canResumeRenewal: boolean;
  canUpdatePayment: boolean;
}

export interface BillingService {
  // Lecture
  getCurrentSubscription(): Promise<Subscription>;
  getAvailablePlans(): Promise<SubscriptionPlan[]>;
  getInvoices(): Promise<Invoice[]>;

  // Actions
  changePlan(planId: string): Promise<void>;
  cancelRenewal(): Promise<void>;
  resumeRenewal(): Promise<void>;
  updatePaymentMethod(paymentMethodId: string): Promise<void>;
}

export interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  createdAt: Date;
  dueDate?: Date;
  pdfUrl?: string;
}

export type InvoiceStatus = "paid" | "open" | "void" | "uncollectible";
